import {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { PlayerState, PlayerAction, Episode, Podcast } from '@/types';

const initialState: PlayerState = {
  episode: null,
  podcast: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isLoading: false,
  error: null,
};

function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case 'PLAY':
      return {
        ...state,
        episode: action.episode,
        podcast: action.podcast,
        isPlaying: true,
        currentTime: 0,
        duration: 0,
        isLoading: true,
        error: null,
      };
    case 'PAUSE':
      return { ...state, isPlaying: false };
    case 'RESUME':
      return { ...state, isPlaying: true, error: null };
    case 'STOP':
      return { ...initialState, volume: state.volume };
    case 'SET_TIME':
      return { ...state, currentTime: action.time };
    case 'SET_DURATION':
      return { ...state, duration: action.duration };
    case 'SET_VOLUME':
      return { ...state, volume: action.volume };
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };
    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };
    default:
      return state;
  }
}

interface PlayerContextValue {
  state: PlayerState;
  play: (episode: Episode, podcast: Podcast) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(playerReducer, initialState);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = 'metadata';

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      dispatch({ type: 'SET_TIME', time: audio.currentTime });
    };

    const handleDurationChange = () => {
      dispatch({ type: 'SET_DURATION', duration: audio.duration });
    };

    const handleCanPlay = () => {
      dispatch({ type: 'SET_LOADING', loading: false });
    };

    const handleWaiting = () => {
      dispatch({ type: 'SET_LOADING', loading: true });
    };

    const handleEnded = () => {
      dispatch({ type: 'PAUSE' });
    };

    const handleError = (e: Event) => {
      const audioEl = e.target as HTMLAudioElement;
      const error = audioEl.error;
      let errorMessage = 'Failed to load audio';

      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Playback aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio format not supported';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio source not supported';
            break;
        }
        console.error('[Podcast Player] Audio error:', errorMessage, error);
      }

      dispatch({ type: 'SET_ERROR', error: errorMessage });
      dispatch({ type: 'PAUSE' });
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Handle play state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.isPlaying && state.episode) {
      audio.play().catch((err) => {
        console.error('[Podcast Player] Play failed:', err);
        // Don't set error for AbortError (happens when quickly switching episodes)
        if (err.name !== 'AbortError') {
          dispatch({ type: 'SET_ERROR', error: 'Failed to play audio. Try again.' });
        }
        dispatch({ type: 'PAUSE' });
      });
    } else {
      audio.pause();
    }
  }, [state.isPlaying, state.episode]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.volume;
    }
  }, [state.volume]);

  const play = useCallback((episode: Episode, podcast: Podcast) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Validate enclosure URL
    if (!episode.enclosureUrl) {
      console.error('[Podcast Player] No audio URL for episode:', episode.title);
      // Still set episode/podcast so player shows, but with error
      dispatch({ type: 'PLAY', episode, podcast });
      dispatch({ type: 'SET_ERROR', error: 'No audio URL available for this episode' });
      return;
    }

    console.log('[Podcast Player] Playing:', episode.title, episode.enclosureUrl);

    audio.src = episode.enclosureUrl;
    audio.load();
    dispatch({ type: 'PLAY', episode, podcast });
  }, []);

  const pause = useCallback(() => {
    dispatch({ type: 'PAUSE' });
  }, []);

  const resume = useCallback(() => {
    dispatch({ type: 'RESUME' });
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    dispatch({ type: 'STOP' });
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      dispatch({ type: 'SET_TIME', time });
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    dispatch({ type: 'SET_VOLUME', volume: Math.max(0, Math.min(1, volume)) });
  }, []);

  return (
    <PlayerContext.Provider
      value={{ state, play, pause, resume, stop, seek, setVolume }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}
